import { useState } from 'react';
import { resetGlobalFilters, setGlobalFilter } from '../store/slice/global-filters';
import { resetGeoFilters } from '../store/slice/filter-slice';
import { useAppDispatch } from './useAppDispatch';
import { setSearchInput } from '../store/slice/health-monitor/device-list';
import { resetSearch } from '../store/slice/date-slice';

/**
 * useFilterDropdown - Custom hook for managing filter dropdown state and actions.
 *
 * This hook provides a reusable way to handle the state and logic of dropdown filters,
 * including opening/closing dropdowns, tracking the last closed dropdown, updating filter
 * values in Redux, and resetting filters.
 *
 * Returns an object containing:
 *
 * @property { { type: string; index: number } | null } openDropdown
 *   - The currently open dropdown, represented by its type and index.
 *   - null if no dropdown is open.
 *
 * @property { { type: string; index: number } | null } justClosed
 *   - Tracks the last dropdown that was just closed.
 *   - Useful for UI animations or conditional logic after a dropdown closes.
 *
 * @property {function(string, number): void} toggleDropdown
 *   - Toggles the open/closed state of a dropdown.
 *   - If the dropdown is already open, it closes it and updates justClosed.
 *   - If it is closed, it opens it and clears justClosed.
 *
 * @property {function({ [key: string]: string }): void} updateFilters
 *   - Updates multiple global filter values in Redux at once.
 *   - Accepts an object with key-value pairs of filters.
 *
 * @property {function(): void} handleResetFilters
 *   - Resets all global filters and geo filters in Redux.
 *
 * @property {function(string, number): boolean} isOpen
 *   - Checks whether a specific dropdown (by type and index) is currently open.
 *   - Returns true if it is open, false otherwise.
 *
 * @property {function} setOpenDropdown
 *   - Directly set the openDropdown state.
 *
 * @property {function} setJustClosed
 *   - Directly set the justClosed state.
 *
 */

export function useFilterDropdown() {
  const dispatch = useAppDispatch();

  const [openDropdown, setOpenDropdown] = useState<{ type: string; index: number } | null>(null);
  const [justClosed, setJustClosed] = useState<{ type: string; index: number } | null>(null);

  const toggleDropdown = (type: string, index: number) => {
    if (openDropdown?.type === type && openDropdown?.index === index) {
      setJustClosed({ type, index });
      setOpenDropdown(null);
    } else {
      setJustClosed(null);
      setOpenDropdown({ type, index });
    }
  };

  const updateFilters = (newFilters: { [key: string]: string }) => {
    Object.entries(newFilters).forEach(([key, value]) => {
      dispatch(setGlobalFilter({ key: key as any, value }));
    });
  };

  const handleResetFilters = () => {
    dispatch(resetGlobalFilters());
    dispatch(resetGeoFilters());
    dispatch(setSearchInput(""));
    dispatch(resetSearch());
  };

  const isOpen = (type: string, index: number) => openDropdown?.type === type && openDropdown?.index === index;

  return {
    openDropdown,
    justClosed,
    toggleDropdown,
    handleResetFilters,
    updateFilters,
    setOpenDropdown,
    setJustClosed,
    isOpen,
  };
}
